import { questions } from './content/faq.js';
import { resolveAssetUrl } from './assetUrl.js';
import { minutesToIsoDuration, parseDurationMinutes } from './recipeTime.js';
import { tagNamesInGroup } from './recipeTags.js';
import { caloriesPerServing, recipeMacros } from './recipeDetails.js';
import { APP_SCREENS } from './appScreens.js';
import { reviewStructuredData } from './recipeComments.js';
import { recipeAuthorStructuredData } from './recipeAuthor.js';
import { STORE_URLS } from './storeLinks.js';

const SITE_URL = 'https://hooshpaz.ir';
const SITE_NAME = 'هوش‌پز';

// Persian readers type this brand three different ways: with a ZWNJ ("هوش‌پز"), with a plain
// space ("هوش پز") and joined ("هوشپز"). Search engines index them as distinct strings, and a
// competing app ships under the joined spelling, so every variant is declared explicitly rather
// than relying on the engine to normalize them for us.
const BRAND_ALIASES = ['هوش پز', 'هوشپز', 'هوش‌پز', 'Hooshpaz', 'hooshpaz.ir'];

const SOCIAL_IMAGE = `${SITE_URL}/og-hooshpaz.png`;
const SOCIAL_IMAGE_WIDTH = 1200;
const SOCIAL_IMAGE_HEIGHT = 630;
const LOGO_IMAGE = `${SITE_URL}/hooshpaz-logo.webp`;

const pages = {
    '/': {
        title: 'هوش‌پز | دستیار آشپزی هوشمند و ساخت دستور پخت با هوش مصنوعی',
        description: 'هوش‌پز (هوش پز) دستیار آشپزی هوشمند فارسی است؛ مواد اولیه را بنویس یا عکس بگیر تا هوش مصنوعی آشپزی یک دستور پخت کامل و متناسب با رژیم، حساسیت و زمان تو بسازد.',
        keywords: 'هوش پز, هوشپز, دستور پخت, دستیار آشپزی هوشمند, هوش مصنوعی آشپزی, دستور پخت با هوش مصنوعی, چی بپزم, آشپزی با مواد موجود',
        breadcrumb: null,
    },
    '/about': {
        title: 'درباره هوش‌پز | دستیار آشپزی هوشمند فارسی',
        description: 'هوش‌پز چیست و چرا ساخته شد؟ داستان دستیار آشپزی هوشمند فارسی که از مواد موجود در آشپزخانه‌ات دستور پخت شخصی‌سازی‌شده می‌سازد.',
        keywords: 'درباره هوش پز, هوشپز چیست, دستیار آشپزی هوشمند, هوش مصنوعی آشپزی',
        breadcrumb: 'درباره هوش‌پز',
    },
    '/faq': {
        title: 'سوالات متداول هوش‌پز | اپ دستور پخت با هوش مصنوعی',
        description: 'پاسخ سوال‌های رایج درباره ساخت دستور پخت با هوش مصنوعی، تشخیص مواد از عکس، رژیم غذایی، ویرایش هوشمند و نصب اپلیکیشن هوش‌پز (هوش پز).',
        keywords: 'سوالات متداول هوش پز, دستور پخت با هوش مصنوعی, تشخیص مواد از عکس, اپلیکیشن آشپزی',
        breadcrumb: 'سوالات متداول',
    },
    '/privacy': {
        title: 'حریم خصوصی هوش‌پز | نحوه پردازش و نگهداری اطلاعات',
        description: 'سیاست حریم خصوصی هوش‌پز و توضیح شفاف درباره پردازش عکس مواد اولیه، اطلاعات حساب، تاریخچه دستورها و سرویس‌های همکار.',
        keywords: 'حریم خصوصی هوش پز, پردازش تصویر مواد اولیه, اطلاعات کاربران',
        breadcrumb: 'حریم خصوصی',
    },
    '/terms': {
        title: 'شرایط استفاده از هوش‌پز | قوانین اپلیکیشن',
        description: 'شرایط استفاده از اپلیکیشن هوش‌پز، حساب کاربری، بسته‌های اعتبار، خرید درون‌برنامه‌ای و دستورهای تولیدشده با هوش مصنوعی.',
        keywords: 'شرایط استفاده هوش پز, قوانین اپلیکیشن آشپزی, بسته اعتبار',
        breadcrumb: 'شرایط استفاده',
    },
    '/contact': {
        title: 'تماس با هوش‌پز | پشتیبانی اپلیکیشن آشپزی هوشمند',
        description: 'برای سوال درباره اپ، حساب کاربری، اعتبار و خرید، حریم خصوصی یا ارائه پیشنهاد با پشتیبانی هوش‌پز (هوش پز) تماس بگیرید.',
        keywords: 'تماس با هوش پز, پشتیبانی اپلیکیشن آشپزی, ارتباط با ما',
        breadcrumb: 'تماس با ما',
    },
    '/recipes': {
        title: 'دستور پخت‌های هوش‌پز | مجموعه دستور پخت فارسی',
        description: 'مجموعه دستور پخت‌های منتخب هوش‌پز؛ نوشته سرآشپزها یا ساخته‌شده با هوش مصنوعی، قابل فیلتر بر اساس دسته‌بندی، مواد اولیه و نویسنده.',
        keywords: 'دستور پخت, دستور پخت فارسی, دستور پخت با هوش مصنوعی, طرز تهیه غذا, هوش پز',
        breadcrumb: 'دستور پخت‌ها',
    },
};

const normalizePath = (url) => {
    const pathname = new URL(url, SITE_URL).pathname;
    return pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname;
};

// Recipe slugs are Persian. Crawlers accept either form, but the canonical, the structured data
// and the sitemap should name the page by the same, percent-encoded, URL.
const encodeUrl = (url) => {
    if (!url) return url;
    try {
        return encodeURI(decodeURI(url));
    } catch {
        return url;
    }
};

const uniqueList = (items) => [...new Set(items.map((item) => String(item).trim()).filter(Boolean))];

const recipeImage = (recipe) => recipe.seo?.ogImageUrl || resolveAssetUrl(recipe.coverImageUrl) || undefined;

// Google's Recipe rich result needs name and image; author, datePublished, description,
// keywords, recipeCategory, recipeCuisine, times and nutrition are what it recommends.
const recipeStructuredData = (recipe, canonical, tagGroups = [], commentThreads = []) => {
    const instructions = recipe.steps?.length
        ? recipe.steps.map((step) => step.text)
        : (recipe.instructions || '').split('\n').filter(Boolean);
    const calories = caloriesPerServing(recipe);
    const macros = recipeMacros(recipe);
    const prepMinutes = parseDurationMinutes(recipe.prepTime);
    const cookMinutes = parseDurationMinutes(recipe.cookTime);
    const cuisineNames = tagNamesInGroup(tagGroups, 'cuisine');
    const cuisines = (recipe.tags || []).filter((tag) => cuisineNames.has(tag));
    const keywords = uniqueList([...(recipe.tags || []), ...(recipe.seo?.keywords || [])]);
    const image = recipeImage(recipe);

    return {
        '@context': 'https://schema.org',
        '@type': 'Recipe',
        name: recipe.title,
        url: canonical,
        image: image ? [image] : undefined,
        description: recipe.seo?.description || recipe.instructions || undefined,
        author: recipeAuthorStructuredData(recipe, { '@type': 'Organization', name: SITE_NAME, url: `${SITE_URL}/` }),
        datePublished: recipe.publishedAt || undefined,
        keywords: keywords.length ? keywords.join(', ') : undefined,
        recipeCategory: recipe.category?.name || undefined,
        recipeCuisine: cuisines.length ? cuisines.join(', ') : undefined,
        recipeIngredient: [...(recipe.ingredients || []), ...(recipe.additionalIngredients || [])],
        recipeInstructions: instructions.map((text) => ({ '@type': 'HowToStep', text })),
        prepTime: minutesToIsoDuration(prepMinutes),
        cookTime: minutesToIsoDuration(cookMinutes),
        totalTime: minutesToIsoDuration(prepMinutes + cookMinutes),
        recipeYield: recipe.servings ? `${recipe.servings} نفر` : undefined,
        nutrition: calories > 0 || macros.length ? {
            '@type': 'NutritionInformation',
            calories: calories > 0 ? `${calories} calories` : undefined,
            ...Object.fromEntries(macros.map((macro) => [macro.schemaProperty, `${macro.grams} g`])),
        } : undefined,
        ...reviewStructuredData(recipe, commentThreads),
        inLanguage: 'fa-IR',
    };
};

const sharedRecipeDescription = (recipe) => {
    const ingredients = [...(recipe.ingredients || []), ...(recipe.additionalIngredients || [])]
        .slice(0, 4)
        .join('، ');
    return ingredients
        ? `دستور پخت ${recipe.title} با مواد لازم ${ingredients}؛ مراحل کامل این دستور را در هوش‌پز ببینید.`
        : `مواد لازم و مراحل کامل دستور پخت ${recipe.title} را در هوش‌پز ببینید.`;
};

const recipeBookListStructuredData = (recipes, canonical) => recipes.length ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url: canonical,
    itemListElement: recipes.map((recipe, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: encodeUrl(`${SITE_URL}/recipes/${recipe.slug}`),
        name: recipe.title,
    })),
} : null;

// Breadcrumb markup is what lets a result show «هوش‌پز › دستور پخت‌ها › …» instead of a bare
// URL, and it gives crawlers an explicit parent for every deep recipe page.
const breadcrumbStructuredData = (trail) => trail.length > 1 ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
    })),
} : null;

const homeCrumb = { name: 'هوش‌پز', url: `${SITE_URL}/` };
const recipesCrumb = { name: 'دستور پخت‌ها', url: `${SITE_URL}/recipes` };

const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
    })),
};

const applicationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    alternateName: BRAND_ALIASES,
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Android',
    image: LOGO_IMAGE,
    screenshot: Object.values(APP_SCREENS).map((screen) => `${SITE_URL}${screen.src}`),
    url: SITE_URL,
    downloadUrl: STORE_URLS,
    description: pages['/'].description,
    inLanguage: 'fa-IR',
    author: { '@type': 'Organization', name: SITE_NAME, url: `${SITE_URL}/` },
    offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'IRR',
        availability: 'https://schema.org/InStock',
    },
};

const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    alternateName: BRAND_ALIASES,
    url: `${SITE_URL}/`,
    logo: LOGO_IMAGE,
    image: SOCIAL_IMAGE,
    description: pages['/'].description,
    sameAs: STORE_URLS,
    areaServed: 'IR',
    contactPoint: [{
        '@type': 'ContactPoint',
        contactType: 'customer support',
        url: `${SITE_URL}/contact`,
        availableLanguage: ['fa'],
    }],
};

const websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    alternateName: BRAND_ALIASES,
    url: `${SITE_URL}/`,
    inLanguage: 'fa-IR',
    publisher: { '@type': 'Organization', name: SITE_NAME, url: `${SITE_URL}/` },
    potentialAction: {
        '@type': 'SearchAction',
        target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_URL}/recipes?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
    },
};

const canonicalFor = (suffix) => `${SITE_URL}${suffix === '/' ? '/' : suffix}`;

const getRecipeBookDetailSeo = (recipeBook) => {
    if (recipeBook.status === 'ready') {
        const recipe = recipeBook.recipe;
        const backendSeo = recipe.seo || {};
        const canonical = encodeUrl(backendSeo.canonicalUrl || canonicalFor(`/recipes/${recipe.slug}`));
        const categoryCrumb = recipe.category?.slug && recipe.category?.name
            ? [{ name: recipe.category.name, url: encodeUrl(canonicalFor(`/recipes/category/${recipe.category.slug}`)) }]
            : [];
        const breadcrumb = breadcrumbStructuredData([homeCrumb, recipesCrumb, ...categoryCrumb, { name: recipe.title, url: canonical }]);
        const keywords = Array.isArray(backendSeo.keywords) ? backendSeo.keywords.join(', ') : backendSeo.keywords;

        return {
            title: backendSeo.title || `${recipe.title} | دستور پخت هوش‌پز`,
            description: backendSeo.description || sharedRecipeDescription(recipe),
            keywords: keywords || `${recipe.title}, دستور پخت, طرز تهیه, هوش‌پز`,
            canonical,
            robots: 'index, follow',
            ogType: 'article',
            image: recipeImage(recipe) || SOCIAL_IMAGE,
            imageAlt: recipe.title,
            twitterCard: 'summary_large_image',
            structuredData: [recipeStructuredData(recipe, canonical, recipeBook.tagGroups, recipeBook.comments?.comments), breadcrumb].filter(Boolean),
            statusCode: 200,
        };
    }

    return {
        title: 'دستور پخت پیدا نشد | هوش‌پز',
        description: 'این دستور پخت پیدا نشد یا از مجموعه هوش‌پز حذف شده است.',
        canonical: canonicalFor(`/recipes/${recipeBook.slug}`),
        robots: 'noindex, nofollow',
        ogType: 'website',
        image: SOCIAL_IMAGE,
        imageAlt: SITE_NAME,
        twitterCard: 'summary_large_image',
        structuredData: [],
        statusCode: recipeBook.status === 'not_found' ? 404 : 200,
    };
};

const getRecipeBookListSeo = (recipeBook) => {
    const category = recipeBook.categories?.find((item) => item.slug === recipeBook.categorySlug);
    const canonical = encodeUrl(canonicalFor(recipeBook.categorySlug ? `/recipes/category/${recipeBook.categorySlug}` : '/recipes'));
    const title = category ? `دستور پخت‌های ${category.name} | هوش‌پز` : pages['/recipes'].title;
    const description = category
        ? `دستور پخت‌های منتخب دسته «${category.name}» در هوش‌پز؛ نوشته سرآشپزها یا ساخته‌شده با هوش مصنوعی، همراه مواد لازم و مراحل کامل.`
        : pages['/recipes'].description;
    const keywords = category
        ? `دستور پخت ${category.name}, طرز تهیه ${category.name}, دستور پخت فارسی, هوش‌پز`
        : pages['/recipes'].keywords;
    const list = recipeBookListStructuredData(recipeBook.recipes || [], canonical);
    const trail = category
        ? [homeCrumb, recipesCrumb, { name: category.name, url: canonical }]
        : [homeCrumb, recipesCrumb];
    const breadcrumb = breadcrumbStructuredData(trail);

    return {
        title,
        description,
        keywords,
        canonical,
        robots: 'index, follow',
        ogType: 'website',
        image: SOCIAL_IMAGE,
        imageAlt: SITE_NAME,
        twitterCard: 'summary_large_image',
        structuredData: [list, breadcrumb].filter(Boolean),
        statusCode: 200,
    };
};

export const getSeo = (url, initialData = null) => {
    const recipeBook = initialData?.recipeBook;
    if (recipeBook?.type === 'detail') return getRecipeBookDetailSeo(recipeBook);
    if (recipeBook?.type === 'list') return getRecipeBookListSeo(recipeBook);

    const path = normalizePath(url);
    const isSharedRecipe = path.startsWith('/sh/');
    const sharedRecipe = initialData?.sharedRecipe;
    const readyRecipe = sharedRecipe?.status === 'ready' ? sharedRecipe.recipe : null;
    const page = pages[path];
    const isNotFound = !page && !isSharedRecipe;
    const canonicalPath = page || isSharedRecipe ? path : '/';
    const canonical = canonicalFor(canonicalPath);
    const sharedStatusCode = sharedRecipe?.status === 'expired'
        ? 410
        : sharedRecipe?.status === 'invalid' ? 404 : 200;
    const staticBreadcrumb = page?.breadcrumb
        ? breadcrumbStructuredData([homeCrumb, { name: page.breadcrumb, url: canonical }])
        : null;

    const pageStructuredData = path === '/'
        ? [websiteStructuredData, organizationStructuredData, applicationStructuredData, faqStructuredData]
        : path === '/faq'
        ? [faqStructuredData, staticBreadcrumb].filter(Boolean)
        : [staticBreadcrumb].filter(Boolean);

    return {
        ...(page || {
            title: readyRecipe ? `${readyRecipe.title} | دستور پخت هوش‌پز` : isSharedRecipe ? 'دستور پخت اشتراکی | هوش‌پز' : 'صفحه پیدا نشد | هوش‌پز',
            description: readyRecipe ? sharedRecipeDescription(readyRecipe) : isSharedRecipe
                ? 'مشاهده دستور پختی که با اپلیکیشن هوش‌پز به اشتراک گذاشته شده است.'
                : 'صفحه‌ای که به دنبال آن بودید پیدا نشد.',
        }),
        canonical,
        robots: isSharedRecipe || isNotFound ? 'noindex, nofollow' : 'index, follow',
        ogType: readyRecipe ? 'article' : 'website',
        image: SOCIAL_IMAGE,
        imageAlt: SITE_NAME,
        twitterCard: 'summary_large_image',
        structuredData: readyRecipe
            ? [recipeStructuredData(readyRecipe, canonical)]
            : pageStructuredData,
        statusCode: isNotFound ? 404 : isSharedRecipe ? sharedStatusCode : 200,
    };
};

export const serializeInitialData = (initialData) => JSON.stringify(initialData)
    .replaceAll('&', '\\u0026')
    .replaceAll('<', '\\u003c')
    .replaceAll('>', '\\u003e')
    .replaceAll(' ', '\\u2028')
    .replaceAll(' ', '\\u2029');

const escapeAttribute = (value) => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

// JSON-LD carries customer text (recipe titles, ingredients, author and reviewer names), so nothing
// in it may close the <script> element or open a comment: the same escaping as the initial data.
export const jsonForHtml = (value) => serializeInitialData(value);

// The social image only carries width/height hints when it is the site's own card, whose
// dimensions we know; a backend-supplied recipe cover can be any size, and wrong hints are
// worse than none.
const isDefaultSocialImage = (seo) => (seo.image || SOCIAL_IMAGE) === SOCIAL_IMAGE;

export const buildSeoHead = (seo) => `
    <title>${escapeAttribute(seo.title)}</title>
    <meta name="description" content="${escapeAttribute(seo.description)}" />
    ${seo.keywords ? `<meta name="keywords" content="${escapeAttribute(seo.keywords)}" />` : ''}
    <meta name="robots" content="${seo.robots}" />
    <meta name="googlebot" content="${seo.robots}" />
    <link rel="canonical" href="${seo.canonical}" />
    <meta property="og:type" content="${seo.ogType || 'website'}" />
    <meta property="og:site_name" content="${SITE_NAME}" />
    <meta property="og:locale" content="fa_IR" />
    <meta property="og:url" content="${seo.canonical}" />
    <meta property="og:title" content="${escapeAttribute(seo.title)}" />
    <meta property="og:description" content="${escapeAttribute(seo.description)}" />
    <meta property="og:image" content="${escapeAttribute(seo.image || SOCIAL_IMAGE)}" />
    <meta property="og:image:alt" content="${escapeAttribute(seo.imageAlt || 'نشان هوش‌پز')}" />${isDefaultSocialImage(seo) ? `
    <meta property="og:image:width" content="${SOCIAL_IMAGE_WIDTH}" />
    <meta property="og:image:height" content="${SOCIAL_IMAGE_HEIGHT}" />` : ''}
    <meta name="twitter:card" content="${seo.twitterCard || 'summary_large_image'}" />
    <meta name="twitter:title" content="${escapeAttribute(seo.title)}" />
    <meta name="twitter:description" content="${escapeAttribute(seo.description)}" />
    <meta name="twitter:image" content="${escapeAttribute(seo.image || SOCIAL_IMAGE)}" />
    ${seo.structuredData.map((data, index) => `<script id="structured-data-${index}" type="application/ld+json">${jsonForHtml(data)}</script>`).join('\n    ')}
`;

export const applySeoToDocument = (seo) => {
    document.title = seo.title;

    const upsertMeta = (selector, attribute, value, content) => {
        let element = document.head.querySelector(selector);
        if (!element) {
            element = document.createElement('meta');
            element.setAttribute(attribute, value);
            document.head.appendChild(element);
        }
        element.setAttribute('content', content);
    };

    upsertMeta('meta[name="description"]', 'name', 'description', seo.description);
    upsertMeta('meta[name="keywords"]', 'name', 'keywords', seo.keywords || '');
    upsertMeta('meta[name="robots"]', 'name', 'robots', seo.robots);
    upsertMeta('meta[name="googlebot"]', 'name', 'googlebot', seo.robots);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', seo.ogType || 'website');
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', seo.canonical);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', seo.title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', seo.description);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', seo.image || SOCIAL_IMAGE);
    upsertMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', seo.imageAlt || 'نشان هوش‌پز');
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', seo.twitterCard || 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', seo.image || SOCIAL_IMAGE);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
    }
    canonical.href = seo.canonical;

    document.head.querySelectorAll('script[id^="structured-data-"]').forEach((element) => element.remove());
    seo.structuredData.forEach((data, index) => {
        const script = document.createElement('script');
        script.id = `structured-data-${index}`;
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
    });
};
